/**
 * Viewer-count design-store catalog. Mirrors the other widget catalogs: a static
 * gallery of self-contained card SVGs the user can apply to the Viewers
 * (VIEWERS) widget. Recreated 1:1 from the Claude Design
 * `Viewer Count Widgets.html` handoff (10 variants), cropped from the original
 * 1920×1080 scenes (the `translate(640 …)` card group → a tight card) and
 * re-parametrised so the live viewer count drives the readout(s).
 *
 * Each design is now PURE DATA ({@link SerializableDesign}): the per-design
 * `build()` JS is gone — the card SVG lives in `frame` with `{{name}}`
 * placeholders, and the live counts are supplied as `slots`. The generic
 * {@link renderDesign} fills them in. This makes every design transmittable,
 * DB-storable and SAFE TO PUBLISH (no JS is ever transported or run).
 *
 * The only dynamic pieces are NUMBERS: the combined `total` (every design) and,
 * for the "multi" restream panel, the per-platform counts. Everything else
 * (labels, glyphs, decoration) is fixed, matching the mock — so no XML-escaping
 * is needed. Numbers use the slot `format`: "thousands" (dot-grouped, e.g.
 * "1.248") for the readouts, and "plain" (round) for the pixel-arcade count.
 * `renderViewersDesignSvg(design, parts)` is the single entry point the Store
 * thumbnail, the config preview and the live Pixi renderer all call.
 *
 * Card-SVG conventions (shared with the other catalogs):
 *  - viewBox is `0 0 cardWidth cardHeight`; NO width/height attrs (the raster
 *    injects them).
 *  - every `<defs>`/gradient/filter/pattern/`<g id>`/glyph id is prefixed with
 *    the design id (e.g. `dark-vcd-panel`) so 10 cards inline in one DOM
 *    document without id collisions; every `url(#…)`/`href="#…"` ref matches.
 *
 * Known caveat: the Cyberpunk (`視聴者`), Anime VHS (`視聴者`/`視聴中`) and assorted
 * emoji (🌿) keep the handoff's accents, but the display fonts (Orbitron / VT323
 * / Audiowide …) carry no CJK/emoji glyphs, so those fall back to a system face
 * in the raster — intentional, matches the mock.
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type { SerializableDesign } from "../entities/designs/serializableDesign";

export type ViewersDesignCategory = "viewers";

/** The numbers a viewer card can display. `total` is the combined count. */
export interface ViewersParts {
  total: number;
  twitch: number;
  youtube: number;
  kick: number;
  restream: number;
  facebook: number;
}

/** A viewer card design — pure data; `renderDesign` turns it into the SVG. */
export type ViewersDesign = SerializableDesign & {
  widgetType: "viewers";
  category: ViewersDesignCategory;
};

// ── Reusable glyph paths (from the mock's shared <defs>) ───────────────────
const G_EYE =
  "M20 4 C8 4 1 14 0 16 C1 18 8 28 20 28 C32 28 39 18 40 16 C39 14 32 4 20 4 Z M20 9 a7 7 0 1 1 0 14 a7 7 0 0 1 0 -14 Z";
const G_TWITCH =
  "M4 0 L0 4 v22 h7 v4 h4 l4 -4 h6 l9 -9 V0 Z M30 16 l-5 5 h-6 l-4 4 v-4 H10 V3 h20 Z M24 7 v9 h-3 V7 Z M17 7 v9 h-3 V7 Z";
const G_YT =
  "M31 5 a4 4 0 0 0 -3 -3 C25 1 16 1 16 1 s-9 0 -12 1 a4 4 0 0 0 -3 3 C0 8 0 12 0 12 s0 4 1 7 a4 4 0 0 0 3 3 c3 1 12 1 12 1 s9 0 12 -1 a4 4 0 0 0 3 -3 c1 -3 1 -7 1 -7 s0 -4 -1 -7 Z M13 17 V7 l8 5 Z";

/** Slot for a dot-grouped thousands readout (e.g. 1248 → "1.248"). */
const totalThousands = { kind: "number", format: "thousands" } as const;

// ── 01 · Dark Minimal ──────────────────────────────────────────────────────
const darkDesign: ViewersDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Eye chip",
  widgetType: "viewers",
  category: "viewers",
  description:
    'Charcoal chip with a live red dot, eye glyph, big mono viewer count and a small "EN VIVO" label.',
  swatches: ["#0d0d12", "#15151d", "#ff4d4d", "#3ad96b"],
  cardWidth: 640,
  cardHeight: 240,
  fonts: ["DM Mono", "Inter"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 640 240" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="dark-vcd-panel" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#16161e"/><stop offset="100%" stop-color="#0b0b10"/></linearGradient>
    <filter id="dark-vcd-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="14" stdDeviation="22" flood-color="#000" flood-opacity=".55"/></filter>
    <path id="dark-vcd-eye" d="${G_EYE}"/>
  </defs>
  <rect width="640" height="240" rx="34" fill="url(#dark-vcd-panel)" stroke="#26262f" stroke-width="1.5" filter="url(#dark-vcd-shadow)"/>
  <rect x="1.5" y="1.5" width="637" height="76" rx="32" fill="#fff" opacity=".03"/>
  <g transform="translate(48 52)"><circle r="8" fill="#ff4d4d"/><circle r="13" fill="none" stroke="#ff4d4d" stroke-width="1.5" opacity=".5"/></g>
  <text x="76" y="60" font-family="DM Mono, monospace" font-weight="500" font-size="22" fill="#8a8f9c" letter-spacing="6">EN VIVO</text>
  <g transform="translate(48 110)" fill="#a07dff"><use href="#dark-vcd-eye" transform="scale(1.6)"/></g>
  <text x="130" y="184" font-family="DM Mono, monospace" font-weight="500" font-size="100" fill="#f5f5f5">{{total}}</text>
  <text x="592" y="184" text-anchor="end" font-family="Inter, sans-serif" font-weight="600" font-size="24" fill="#8a8f9c">espectadores</text>
</svg>`,
};

// ── 02 · Multi-Platform ────────────────────────────────────────────────────
const multiDesign: ViewersDesign = {
  id: "multi",
  name: "Multi-Platform",
  subtitle: "Restream pills",
  widgetType: "viewers",
  category: "viewers",
  description:
    "Outlined brand pills (Twitch / YouTube / Kick / Restream / Facebook) each with its glyph and viewer count, plus a combined total.",
  swatches: ["#101014", "#9146ff", "#ff0033", "#53fc18"],
  cardWidth: 640,
  cardHeight: 520,
  fonts: ["Space Grotesk", "DM Mono", "Anton"],
  slots: {
    total: totalThousands,
    twitch: totalThousands,
    youtube: totalThousands,
    kick: totalThousands,
    restream: totalThousands,
    facebook: totalThousands,
  },
  frame: `<svg viewBox="0 0 640 520" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="multi-vcm-shadow" x="-20%" y="-20%" width="140%" height="150%"><feDropShadow dx="0" dy="16" stdDeviation="24" flood-color="#000" flood-opacity=".55"/></filter><path id="multi-vcm-twitch" d="${G_TWITCH}"/><path id="multi-vcm-yt" d="${G_YT}"/></defs>
  <rect width="640" height="520" rx="22" fill="#15131c" stroke="#2a2733" stroke-width="1.5" filter="url(#multi-vcm-shadow)"/>
  <g transform="translate(34 40)"><circle cx="6" cy="6" r="7" fill="#ff4d4d"/><circle cx="6" cy="6" r="11" fill="none" stroke="#ff4d4d" stroke-width="1.5" opacity=".5"/><text x="28" y="12" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="20" fill="#fff" letter-spacing="2">ESPECTADORES EN VIVO</text></g>
  <line x1="34" y1="66" x2="606" y2="66" stroke="#26232f"/>
  <g font-family="Space Grotesk, sans-serif" font-weight="700" font-size="24">
    <g transform="translate(34 84)"><rect width="572" height="62" rx="31" fill="#1b1430" stroke="#9146ff" stroke-width="2"/><g transform="translate(24 16)"><use href="#multi-vcm-twitch" transform="scale(0.94)" fill="#9146ff"/></g><text x="74" y="40" fill="#fff">Twitch</text><text x="548" y="40" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" fill="#bf94ff">{{twitch}}</text></g>
    <g transform="translate(34 156)"><rect width="572" height="62" rx="31" fill="#2a1014" stroke="#ff0033" stroke-width="2"/><g transform="translate(22 19)"><use href="#multi-vcm-yt" transform="scale(1.0)" fill="#ff0033"/></g><text x="74" y="40" fill="#fff">YouTube</text><text x="548" y="40" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" fill="#ff5a76">{{youtube}}</text></g>
    <g transform="translate(34 228)"><rect width="572" height="62" rx="31" fill="#14240c" stroke="#53fc18" stroke-width="2"/><g transform="translate(24 16)"><rect width="30" height="30" rx="6" fill="#53fc18"/><text x="15" y="23" text-anchor="middle" font-family="Anton, sans-serif" font-size="22" fill="#0c0e0d">K</text></g><text x="74" y="40" fill="#fff">Kick</text><text x="548" y="40" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" fill="#7bff52">{{kick}}</text></g>
    <g transform="translate(34 300)"><rect width="572" height="62" rx="31" fill="#171a2e" stroke="#3a6dff" stroke-width="2"/><g transform="translate(24 16)"><rect width="30" height="30" rx="8" fill="#3a6dff"/><text x="15" y="23" text-anchor="middle" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="20" fill="#fff">R</text></g><text x="74" y="40" fill="#fff">Restream</text><text x="548" y="40" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" fill="#8aa6ff">{{restream}}</text></g>
    <g transform="translate(34 372)"><rect width="572" height="62" rx="31" fill="#101c33" stroke="#1877f2" stroke-width="2"/><g transform="translate(24 15)"><circle cx="16" cy="16" r="16" fill="#1877f2"/><path d="M18 10 h3 V6 h-4 c-3 0 -5 2 -5 5 v3 h-3 v4 h3 v8 h4 v-8 h3 l1 -4 h-4 v-2 c0 -1 0 -2 2 -2 Z" fill="#fff"/></g><text x="74" y="40" fill="#fff">Facebook</text><text x="548" y="40" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" fill="#6aa8ff">{{facebook}}</text></g>
  </g>
  <line x1="34" y1="456" x2="606" y2="456" stroke="#26232f"/>
  <text x="34" y="496" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="22" fill="#8a8f9c" letter-spacing="2">TOTAL</text>
  <text x="606" y="496" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" font-size="30" fill="#3ad96b">{{total}}</text>
</svg>`,
};

// ── 03 · Y2K Aqua Gel ──────────────────────────────────────────────────────
const y2kDesign: ViewersDesign = {
  id: "y2k",
  name: "Y2K Aqua Gel",
  subtitle: "Glossy counter",
  widgetType: "viewers",
  category: "viewers",
  description:
    "Glossy aqua capsule with chrome highlight, gel digits and a green LED live indicator.",
  swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
  cardWidth: 640,
  cardHeight: 240,
  fonts: ["Audiowide"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 640 240" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-vcy-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="55%" stop-color="#1c8fff"/><stop offset="100%" stop-color="#003a7a"/></linearGradient>
    <filter id="y2k-vcy-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="14" stdDeviation="20" flood-color="#001026" flood-opacity=".5"/></filter>
    <path id="y2k-vcy-eye" d="${G_EYE}"/>
  </defs>
  <rect width="640" height="240" rx="44" fill="url(#y2k-vcy-bg)" stroke="#fff" stroke-width="3" filter="url(#y2k-vcy-shadow)"/>
  <ellipse cx="320" cy="32" rx="290" ry="12" fill="#fff" opacity=".7"/>
  <g transform="translate(48 56)"><circle r="11" fill="#003a7a"/><circle r="6" fill="#5aff8a"/></g>
  <text x="76" y="64" font-family="Audiowide, sans-serif" font-size="24" fill="#fff" letter-spacing="2">LIVE.NOW</text>
  <text x="592" y="64" text-anchor="end" font-family="Audiowide, sans-serif" font-size="18" fill="#cffff4">VIEWERS</text>
  <g transform="translate(60 116)" fill="#fff"><use href="#y2k-vcy-eye" transform="scale(1.6)"/></g>
  <text x="350" y="180" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="104" fill="#fff">{{total}}</text>
</svg>`,
};

// ── 04 · Cyberpunk Neon ────────────────────────────────────────────────────
const cyberDesign: ViewersDesign = {
  id: "cyber",
  name: "Cyberpunk Neon",
  subtitle: "HUD readout",
  widgetType: "viewers",
  category: "viewers",
  description:
    'Chamfered HUD with "LIVE.FEED" tag, neon Orbitron count, katakana accent and a signal-bar meter.',
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  cardWidth: 640,
  cardHeight: 240,
  fonts: ["DM Mono", "Orbitron"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 640 240" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="cyber-vcc-g"><feGaussianBlur stdDeviation="2.5"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="cyber-vcc-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#00f0ff" opacity=".07"/></pattern>
  </defs>
  <g filter="url(#cyber-vcc-g)">
    <polygon points="0,40 40,0 600,0 640,40 640,200 600,240 40,240 0,200" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="14,46 50,14 590,14 626,46 626,194 590,226 50,226 14,194" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <rect x="14" y="14" width="612" height="212" fill="url(#cyber-vcc-scan)"/>
    <text x="34" y="58" font-family="DM Mono, monospace" font-size="18" fill="#f5d019" letter-spacing="6">▮ LIVE.FEED · 視聴者</text>
    <text x="606" y="58" text-anchor="end" font-family="DM Mono, monospace" font-size="16" fill="#ff2e88">● ON AIR</text>
    <text x="320" y="170" text-anchor="middle" font-family="Orbitron, sans-serif" font-weight="900" font-size="100" fill="#00f0ff">{{total}}</text>
    <g transform="translate(34 200)" fill="#ff2e88"><rect x="0" y="-6" width="8" height="6"/><rect x="14" y="-12" width="8" height="12"/><rect x="28" y="-20" width="8" height="20"/><rect x="42" y="-28" width="8" height="28"/></g>
    <text x="100" y="200" font-family="DM Mono, monospace" font-size="14" fill="#88e8d8" letter-spacing="4">VIEWERS · STABLE · @eriol</text>
  </g>
</svg>`,
};

// ── 05 · Editorial ─────────────────────────────────────────────────────────
const editorialDesign: ViewersDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Circulation",
  widgetType: "viewers",
  category: "viewers",
  description:
    'Cream card with double rule, italic "lectores en vivo" and a serif circulation figure.',
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  cardWidth: 640,
  cardHeight: 240,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 640 240" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="editorial-vce-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="12" stdDeviation="18" flood-color="#000" flood-opacity=".4"/></filter></defs>
  <rect width="640" height="240" fill="#f5efe2" filter="url(#editorial-vce-shadow)"/>
  <line x1="24" y1="24" x2="616" y2="24" stroke="#1a1815" stroke-width="3"/>
  <line x1="24" y1="32" x2="616" y2="32" stroke="#1a1815" stroke-width="1"/>
  <text x="40" y="86" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="36" fill="#8a3a2a">Lectores en vivo</text>
  <text x="40" y="180" font-family="Cormorant Garamond, serif" font-weight="600" font-size="100" fill="#1a1815">{{total}}</text>
  <line x1="430" y1="120" x2="616" y2="120" stroke="#1a1815" stroke-width="1"/>
  <text x="616" y="110" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#8a3a2a" letter-spacing="4">CIRCULACIÓN</text>
  <text x="616" y="180" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#8a3a2a" letter-spacing="4">EDICIÓN EN DIRECTO</text>
</svg>`,
};

// ── 06 · Pixel Arcade ──────────────────────────────────────────────────────
const pixelDesign: ViewersDesign = {
  id: "pixel",
  name: "Pixel Arcade",
  subtitle: "Players online",
  widgetType: "viewers",
  category: "viewers",
  description:
    '8-bit box with "PLAYERS ONLINE", a pixel eye sprite and a chunky Press Start 2P count.',
  swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
  cardWidth: 640,
  cardHeight: 240,
  fonts: ["Press Start 2P"],
  slots: { total: { kind: "number", format: "plain" } },
  frame: `<svg viewBox="0 0 640 240" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
  <rect width="640" height="240" fill="#48cae4"/>
  <rect x="8" y="8" width="624" height="224" fill="#1d2a3a"/>
  <rect x="16" y="16" width="608" height="208" fill="#0a121c"/>
  <g fill="#ffb703"><rect width="24" height="24"/><rect x="616" width="24" height="24"/><rect y="216" width="24" height="24"/><rect x="616" y="216" width="24" height="24"/></g>
  <g transform="translate(40 -16)"><rect width="320" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="308" height="20" fill="#1d2a3a"/><text x="160" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703">PLAYERS ONLINE</text></g>
  <g transform="translate(56 96)" fill="#48cae4"><rect x="12" y="0" width="36" height="6"/><rect x="6" y="6" width="48" height="6"/><rect x="0" y="12" width="60" height="12"/><rect x="6" y="24" width="48" height="6"/><rect x="12" y="30" width="36" height="6"/><rect x="24" y="12" width="12" height="12" fill="#0a121c"/></g>
  <text x="140" y="158" font-family="Press Start 2P, monospace" font-size="44" fill="#ffb703">{{total}}</text>
  <text x="56" y="208" font-family="Press Start 2P, monospace" font-size="12" fill="#48cae4">▲ LIVE</text>
</svg>`,
};

// ── 07 · Cottagecore ───────────────────────────────────────────────────────
const cottageDesign: ViewersDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Gathered round",
  widgetType: "viewers",
  category: "viewers",
  description:
    'Linen card with cross-stitch border, a flower bullet and italic "almas reunidas" with the count.',
  swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  cardWidth: 640,
  cardHeight: 240,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 640 240" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="cottage-vcco-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="cottage-vcco-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <g id="cottage-vcco-fl"><circle r="13" fill="#ff9da8"/><circle cx="-13" cy="-5" r="10" fill="#ff9da8"/><circle cx="13" cy="-5" r="10" fill="#ff9da8"/><circle cx="-8" cy="10" r="10" fill="#ff9da8"/><circle cx="8" cy="10" r="10" fill="#ff9da8"/><circle r="6" fill="#ffd060"/></g>
    <filter id="cottage-vcco-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="12" stdDeviation="18" flood-color="#3a2a14" flood-opacity=".4"/></filter>
  </defs>
  <rect width="640" height="240" fill="url(#cottage-vcco-linen)" stroke="#6b4a2a" stroke-width="2" filter="url(#cottage-vcco-shadow)"/>
  <rect width="640" height="24" fill="url(#cottage-vcco-st)"/>
  <rect y="216" width="640" height="24" fill="url(#cottage-vcco-st)"/>
  <rect width="24" height="240" fill="url(#cottage-vcco-st)"/>
  <rect x="616" width="24" height="240" fill="url(#cottage-vcco-st)"/>
  <use href="#cottage-vcco-fl" transform="translate(80 96)"/>
  <text x="120" y="88" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="34" fill="#5a7a4a">almas reunidas hoy</text>
  <text x="120" y="178" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="92" fill="#2a1a08">{{total}}</text>
  <text x="592" y="178" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#b85a4a" letter-spacing="4">en vivo 🌿</text>
</svg>`,
};

// ── 08 · Brutalist ─────────────────────────────────────────────────────────
const brutalistDesign: ViewersDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Counter slab",
  widgetType: "viewers",
  category: "viewers",
  description:
    'Black slab with orange edge, enormous Anton count and a mono "VIEWERS · EN VIVO" footer.',
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  cardWidth: 654,
  cardHeight: 254,
  fonts: ["Anton", "DM Mono"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 654 254" xmlns="http://www.w3.org/2000/svg">
  <rect x="14" y="14" width="640" height="240" fill="#ff5a2a"/>
  <rect width="640" height="240" fill="#1a1a1a"/>
  <rect width="24" height="240" fill="#ff5a2a"/>
  <text x="48" y="62" font-family="DM Mono, monospace" font-size="16" fill="#ff5a2a" letter-spacing="10">// VIEWERS</text>
  <text x="40" y="186" font-family="Anton, sans-serif" font-size="150" fill="#f5f5f5" letter-spacing="-2">{{total}}</text>
  <text x="600" y="210" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#c8c2b8" letter-spacing="6">EN VIVO</text>
  <g transform="translate(560 38)"><circle r="9" fill="#ff5a2a"/></g>
</svg>`,
};

// ── 09 · Anime VHS ─────────────────────────────────────────────────────────
const animeDesign: ViewersDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "視聴者",
  widgetType: "viewers",
  category: "viewers",
  description:
    "Pink double-stroke panel with cyan tracking ghost, katakana tag and RGB-split VT323 count.",
  swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  cardWidth: 646,
  cardHeight: 240,
  fonts: ["DM Mono", "VT323"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 646 240" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-vca-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="anime-vca-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".12"/></pattern>
  </defs>
  <g filter="url(#anime-vca-rgb)">
    <rect x="6" y="0" width="640" height="240" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="640" height="240" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="620" height="220" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="640" height="240" fill="url(#anime-vca-scan)"/>
    <g><rect width="230" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">視聴者 · LIVE</text></g>
    <text x="610" y="28" text-anchor="end" font-family="VT323, monospace" font-size="24" fill="#7ad0ff">●LIVE</text>
    <text x="320" y="172" text-anchor="middle" font-family="VT323, monospace" font-size="120" fill="#fff7c2">{{total}}</text>
    <text x="320" y="214" text-anchor="middle" font-family="VT323, monospace" font-size="26" fill="#7ad0ff" letter-spacing="6">espectadores · 視聴中</text>
  </g>
</svg>`,
};

// ── 10 · Liquid Glass ──────────────────────────────────────────────────────
const liquidDesign: ViewersDesign = {
  id: "liquid",
  name: "Liquid Glass",
  subtitle: "Frosted counter",
  widgetType: "viewers",
  category: "viewers",
  description:
    "Frosted glass chip over a cobalt gradient with translucent count, eye glyph and a soft live dot.",
  swatches: ["#0a1230", "#1c3a7a", "#9ad4ff", "#ffffff"],
  cardWidth: 640,
  cardHeight: 240,
  fonts: ["Space Grotesk", "DM Mono"],
  slots: { total: totalThousands },
  frame: `<svg viewBox="0 0 640 240" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="liquid-vcl-bg" cx="40%" cy="20%" r="100%"><stop offset="0%" stop-color="#2a4a9a"/><stop offset="60%" stop-color="#0a1230"/><stop offset="100%" stop-color="#020615"/></radialGradient>
    <radialGradient id="liquid-vcl-bok" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#9ad4ff" stop-opacity=".55"/><stop offset="100%" stop-color="#9ad4ff" stop-opacity="0"/></radialGradient>
    <filter id="liquid-vcl-bl"><feGaussianBlur stdDeviation="22"/></filter>
    <filter id="liquid-vcl-shadow" x="-20%" y="-20%" width="140%" height="170%"><feDropShadow dx="0" dy="14" stdDeviation="22" flood-color="#000" flood-opacity=".55"/></filter>
    <path id="liquid-vcl-eye" d="${G_EYE}"/>
  </defs>
  <rect width="640" height="240" rx="34" fill="url(#liquid-vcl-bg)" stroke="rgba(255,255,255,.22)" stroke-width="1.5" filter="url(#liquid-vcl-shadow)"/>
  <g filter="url(#liquid-vcl-bl)" opacity=".6"><circle cx="120" cy="60" r="120" fill="url(#liquid-vcl-bok)"/><circle cx="540" cy="200" r="140" fill="url(#liquid-vcl-bok)"/></g>
  <rect x="1.5" y="1.5" width="637" height="80" rx="32" fill="#fff" opacity=".05"/>
  <g transform="translate(48 52)"><circle r="8" fill="#ff6b6b"/><circle r="13" fill="none" stroke="#ff6b6b" stroke-width="1.5" opacity=".5"/></g>
  <text x="76" y="60" font-family="Space Grotesk, sans-serif" font-weight="600" font-size="22" fill="#eaf4ff" letter-spacing="3">EN VIVO</text>
  <g transform="translate(48 108)" fill="#9ad4ff"><use href="#liquid-vcl-eye" transform="scale(1.6)"/></g>
  <text x="130" y="184" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="100" fill="#fff">{{total}}</text>
  <text x="592" y="184" text-anchor="end" font-family="DM Mono, monospace" font-size="16" fill="#9ad4ff">espectadores</text>
</svg>`,
};

export const VIEWERS_DESIGNS: ViewersDesign[] = [
  darkDesign,
  multiDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  liquidDesign,
];

/** Render a design's card SVG for the given counts. */
export function renderViewersDesignSvg(
  design: ViewersDesign,
  parts: ViewersParts,
): string {
  return renderDesign(design, {
    values: {
      total: parts.total,
      twitch: parts.twitch,
      youtube: parts.youtube,
      kick: parts.kick,
      restream: parts.restream,
      facebook: parts.facebook,
    },
  });
}

export function getViewersDesign(
  id: string | undefined,
): ViewersDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = VIEWERS_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Not a first-party id — fall back to an installed community design so a
  // design the user applied (options.designId = a backend id) still renders
  // live. The registry already constrains by widgetType.
  return getInstalledDesign(id, "viewers") as ViewersDesign | undefined;
}
